import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiFilter, FiMail, FiPhone, FiTrash2, FiCheck, FiRefreshCw, FiPlus } = FiIcons;

const Leads = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [leads, setLeads] = useState([]);
  const [actionMessage, setActionMessage] = useState(null);

  // Initialize Data
  useEffect(() => {
    const storedLeads = localStorage.getItem('ramirez_leads');
    if (storedLeads) {
      setLeads(JSON.parse(storedLeads));
    } else {
      // Default Mock Data
      const defaultLeads = [
        { id: 1, name: "Carlos Rivera", company: "Rivera Transport", email: "carlos@riveratrans.com", phone: "9095550101", type: "Reminder", status: "New", date: "2024-03-15" },
        { id: 2, name: "Sarah Smith", company: "Smith Logistics", email: "sarah@smithlog.com", phone: "9095550123", type: "Quote", status: "Contacted", date: "2024-03-14" },
        { id: 3, name: "Mike Johnson", company: "MJ Trucking", email: "mike@mjtrucks.com", phone: "9095550199", type: "Service", status: "Resolved", date: "2024-03-12" },
        { id: 4, name: "David Chen", company: "Chen Distribution", email: "david@chendist.com", phone: "6265550144", type: "Reminder", status: "New", date: "2024-03-10" },
      ];
      setLeads(defaultLeads);
      localStorage.setItem('ramirez_leads', JSON.stringify(defaultLeads));
    }
  }, []);

  // Save Data on Change
  useEffect(() => {
    if (leads.length >= 0) { // Allow saving empty array
      localStorage.setItem('ramirez_leads', JSON.stringify(leads));
    }
  }, [leads]);

  const showAction = (msg) => {
    setActionMessage(msg);
    setTimeout(() => setActionMessage(null), 3000);
  };

  const handleEmail = (email) => {
    window.location.href = `mailto:${email}`;
    showAction(`Opened email client for ${email}`);
  };

  const handleCall = (phone) => {
    window.location.href = `tel:${phone}`;
    showAction(`Dialing ${phone}...`);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to remove this lead?")) {
      setLeads(prev => prev.filter(lead => lead.id !== id));
      showAction("Lead removed successfully.");
    }
  };

  const handleStatusChange = (id) => {
    setLeads(prev => prev.map(lead => {
      if (lead.id === id) {
        const nextStatus = lead.status === 'New' ? 'Contacted' : lead.status === 'Contacted' ? 'Resolved' : 'New';
         showAction(`Status updated to ${nextStatus}`);
        return { ...lead, status: nextStatus };
      }
      return lead;
    }));
   
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-900/30 text-blue-400 border-blue-800';
      case 'Contacted': return 'bg-yellow-900/30 text-yellow-400 border-yellow-800';
      case 'Resolved': return 'bg-green-900/30 text-green-400 border-green-800';
      default: return 'bg-gray-700 text-gray-300';
    }
  };

  const filteredLeads = leads.filter(lead =>
    lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.company.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Leads & Reminders</h1>
          <p className="text-gray-400">Manage your fleet inquiries and reminder subscriptions.</p>
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-gray-500" />
            <input
              type="text"
              placeholder="Search leads..."
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-10 pr-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {actionMessage && (
        <div className="fixed top-20 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2 animate-fade-in-up">
          <SafeIcon icon={FiCheck} />
          {actionMessage}
        </div>
      )}

      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-900/50 border-b border-gray-700 text-gray-400 text-sm uppercase">
                <th className="px-6 py-4 font-medium">Name / Company</th>
                <th className="px-6 py-4 font-medium">Type</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium">Date Received</th>
                <th className="px-6 py-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredLeads.length > 0 ? (
                filteredLeads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-white">{lead.name}</div>
                      <div className="text-sm text-gray-400">{lead.company}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-700 text-gray-300 border border-gray-600">
                        {lead.type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleStatusChange(lead.id)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border cursor-pointer hover:opacity-80 transition-opacity ${getStatusColor(lead.status)}`}
                        title="Click to cycle status"
                      >
                        {lead.status}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {lead.date}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <button onClick={() => handleEmail(lead.email)} className="text-gray-400 hover:text-blue-400 transition-colors p-1" title="Send Email">
                          <SafeIcon icon={FiMail} />
                        </button>
                        <button onClick={() => handleCall(lead.phone)} className="text-gray-400 hover:text-green-400 transition-colors p-1" title="Call">
                          <SafeIcon icon={FiPhone} />
                        </button>
                        <button onClick={() => handleStatusChange(lead.id)} className="text-gray-400 hover:text-yellow-400 transition-colors p-1" title="Update Status">
                          <SafeIcon icon={FiRefreshCw} />
                        </button>
                        <button onClick={() => handleDelete(lead.id)} className="text-gray-400 hover:text-red-400 transition-colors p-1" title="Delete Lead">
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    No leads found. New submissions will appear here.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Leads;